import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Save, Plus, Trash2, RotateCcw, Loader2, ExternalLink, FileUp, FileText, X, ChevronDown, FolderOpen } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import RichTextEditor from '../../components/RichTextEditor';
import { plainToHtml } from '../../lib/htmlText';

const TITLES = {
  estatutos:          'Estatutos del CMIC',
  'reg-admisiones':   'Reglamento de Admisiones',
  'reg-tesoreria':    'Reglamento de Tesorería',
  'reg-etica':        'Reglamento de Ética y Vigilancia',
  'reg-redes':        'Reglamento del Correo Grupal y Redes Sociales',
  'reg-malintzin':    'Premio Malintzin',
  'reg-solidaridad':  'Fondo de Solidaridad',
};
const PUBLIC_URLS = {
  estatutos:          '/es/estatutos',
  'reg-admisiones':   '/es/reglamentos/admisiones',
  'reg-tesoreria':    '/es/reglamentos/tesoreria',
  'reg-etica':        '/es/reglamentos/etica',
  'reg-redes':        '/es/reglamentos/redes',
  'reg-malintzin':    '/es/reglamentos/malintzin',
  'reg-solidaridad':  '/es/reglamentos/solidaridad',
};

export default function AdminLegalEditor() {
  const { docKey } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [openChapters, setOpenChapters] = useState(new Set([0]));

  async function openPicker() {
    setPickerOpen(true);
    setMediaLoading(true);
    try {
      const { data } = await api.get('/admin/media');
      const docs = (data.files || []).filter((f) => f.type === 'document' && f.extension === 'pdf');
      setMediaFiles(docs);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar los archivos');
      setPickerOpen(false);
    } finally { setMediaLoading(false); }
  }
  function pickExisting(f) {
    setDoc((d) => ({ ...d, pdf_url: f.url, pdf_filename: f.filename }));
    setPickerOpen(false);
    setOk('PDF seleccionado. No olvides "Guardar cambios".');
    setTimeout(() => setOk(''), 5000);
  }

  function toggleChapter(ci) {
    setOpenChapters((prev) => {
      const next = new Set(prev);
      if (next.has(ci)) next.delete(ci); else next.add(ci);
      return next;
    });
  }

  // Migrate stored plain-text article/revision content into HTML so the
  // WYSIWYG editor shows paragraphs correctly. Already-HTML values pass through.
  function hydrateForEditor(d) {
    const chapters = (d.chapters || []).map((c) => ({
      ...c,
      articles: (c.articles || []).map((a) => ({
        ...a,
        es: plainToHtml(a.es || ''),
        en: plainToHtml(a.en || ''),
      })),
    }));
    const revision_note = d.revision_note
      ? { es: plainToHtml(d.revision_note.es || ''), en: plainToHtml(d.revision_note.en || '') }
      : d.revision_note;
    return { ...d, chapters, revision_note };
  }

  async function load() {
    setLoading(true); setError(''); setOk('');
    try {
      const res = await api.get(`/legal/${docKey}`);
      setDoc(hydrateForEditor(res.data));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al cargar');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [docKey]);

  async function save() {
    setSaving(true); setError(''); setOk('');
    try {
      // Clean Quill output before persisting: strip non-breaking spaces so the
      // stored HTML stays tidy (Quill tends to emit &nbsp; between words).
      const clean = (s) => (s || '').replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');
      const cleanLoc = (o) => (o ? { es: clean(o.es), en: clean(o.en) } : o);
      const chapters = (doc.chapters || []).map((c) => ({
        ...c,
        articles: (c.articles || []).map((a) => ({ ...a, es: clean(a.es), en: clean(a.en) })),
      }));
      await api.put(`/admin/legal/${docKey}`, {
        title: doc.title || { es: '', en: '' },
        revision_note: cleanLoc(doc.revision_note) || null,
        chapters,
        pdf_url: doc.pdf_url || null,
        pdf_filename: doc.pdf_filename || null,
        show_download: doc.show_download !== false,
        download_label: doc.download_label || null,
      });
      setOk('Guardado correctamente.');
      setTimeout(() => setOk(''), 4000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al guardar');
    } finally { setSaving(false); }
  }

  async function resetToDefault() {
    if (!window.confirm('¿Restaurar el contenido original? Se descartarán todos los cambios guardados.')) return;
    setSaving(true);
    try {
      await api.post(`/admin/legal/${docKey}/reset`);
      await load();
      setOk('Contenido restaurado a la versión original.');
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setSaving(false); }
  }

  function updateTitle(loc, value) {
    setDoc({ ...doc, title: { ...(doc.title || {}), [loc]: value } });
  }
  function updateRevisionNote(loc, value) {
    setDoc({ ...doc, revision_note: { ...(doc.revision_note || {}), [loc]: value } });
  }
  function updateDownloadLabel(loc, value) {
    setDoc({ ...doc, download_label: { ...(doc.download_label || {}), [loc]: value } });
  }
  async function uploadPdf(file) {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('El archivo debe ser un PDF.'); return;
    }
    setUploadingPdf(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/admin/media/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDoc((d) => ({ ...d, pdf_url: data.url, pdf_filename: data.original_filename || data.filename }));
      setOk('PDF subido. No olvides "Guardar cambios".');
      setTimeout(() => setOk(''), 5000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo subir el PDF');
    } finally { setUploadingPdf(false); if (pdfInputRef.current) pdfInputRef.current.value = ''; }
  }
  function removePdf() {
    setDoc({ ...doc, pdf_url: null, pdf_filename: null });
  }
  function updateChapterTitle(ci, loc, value) {
    const chapters = [...doc.chapters];
    chapters[ci] = { ...chapters[ci], title: { ...(chapters[ci].title || {}), [loc]: value } };
    setDoc({ ...doc, chapters });
  }
  function updateArticle(ci, ai, field, value) {
    const chapters = [...doc.chapters];
    const articles = [...(chapters[ci].articles || [])];
    articles[ai] = { ...articles[ai], [field]: value };
    chapters[ci] = { ...chapters[ci], articles };
    setDoc({ ...doc, chapters });
  }
  function addArticle(ci) {
    const chapters = [...doc.chapters];
    const articles = [...(chapters[ci].articles || []), { number: '', es: '', en: '' }];
    chapters[ci] = { ...chapters[ci], articles };
    setDoc({ ...doc, chapters });
  }
  function removeArticle(ci, ai) {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    const chapters = [...doc.chapters];
    chapters[ci] = { ...chapters[ci], articles: chapters[ci].articles.filter((_, i) => i !== ai) };
    setDoc({ ...doc, chapters });
  }
  function addChapter() {
    const chapters = [...(doc.chapters || []), { title: { es: 'Nuevo capítulo', en: 'New chapter' }, articles: [] }];
    setDoc({ ...doc, chapters });
  }
  function removeChapter(ci) {
    if (!window.confirm(`¿Eliminar el capítulo "${doc.chapters[ci]?.title?.es || ''}" y todos sus artículos?`)) return;
    setDoc({ ...doc, chapters: doc.chapters.filter((_, i) => i !== ci) });
  }

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>;
  }
  if (!doc) {
    return <div className="p-6 text-charcoal-500">Documento no encontrado.</div>;
  }

  const docTitle = TITLES[docKey] || docKey;

  return (
    <div data-testid={`admin-legal-editor-${docKey}`} className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/admin/pages" className="p-2 rounded-lg hover:bg-gray-100" title="Volver"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
              <BookOpen size={22} className="text-cmic-600" /> {docTitle}
            </h1>
            <p className="text-xs text-charcoal-500 mt-0.5">
              {doc.chapters.length} capítulos · {doc.chapters.reduce((acc, c) => acc + (c.articles?.length || 0), 0)} artículos
              {doc.updated_at && <> · última edición {new Date(doc.updated_at).toLocaleString('es-MX')}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={PUBLIC_URLS[docKey]} target="_blank" rel="noopener noreferrer"
            data-testid="legal-preview-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-charcoal-600 hover:bg-gray-100 rounded-lg">
            <ExternalLink size={14} /> Vista previa
          </a>
          <button onClick={resetToDefault} disabled={saving}
            data-testid="legal-reset-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 rounded-lg border border-amber-200">
            <RotateCcw size={14} /> Restaurar original
          </button>
          <button onClick={save} disabled={saving}
            data-testid="legal-save-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-cmic-600 text-white font-medium rounded-lg text-sm hover:bg-cmic-700 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar cambios
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="legal-error">{error}</div>}
      {ok && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg p-3 text-sm" data-testid="legal-ok">{ok}</div>}

      {/* Title + revision note */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Título (Español)">
            <input type="text" value={doc.title?.es || ''} onChange={(e) => updateTitle('es', e.target.value)}
              data-testid="legal-title-es"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Title (English)">
            <input type="text" value={doc.title?.en || ''} onChange={(e) => updateTitle('en', e.target.value)}
              data-testid="legal-title-en"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </Field>
        </div>
        {docKey === 'estatutos' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nota de revisión (Español)">
              <RichTextEditor
                value={doc.revision_note?.es || ''}
                onChange={(html) => updateRevisionNote('es', html)}
                allowMedia={false}
                minHeight={120}
                testid="legal-revnote-es"
                placeholder="Nota de revisión en español"
              />
            </Field>
            <Field label="Revision note (English)">
              <RichTextEditor
                value={doc.revision_note?.en || ''}
                onChange={(html) => updateRevisionNote('en', html)}
                allowMedia={false}
                minHeight={120}
                testid="legal-revnote-en"
                placeholder="Revision note in English"
              />
            </Field>
          </div>
        )}
      </div>

      {/* PDF download button config */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="legal-pdf-config">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-cmic-600" />
          <h3 className="text-sm font-semibold text-charcoal-700">Botón "Descargar PDF"</h3>
        </div>
        <p className="text-xs text-charcoal-500 -mt-1">
          Sube o cambia el archivo PDF que se descarga desde la página pública, edita el texto del botón o escóndelo.
        </p>

        <label className="flex items-center gap-2.5 text-sm text-charcoal-700 cursor-pointer" data-testid="legal-pdf-show-field">
          <input
            type="checkbox"
            checked={doc.show_download !== false}
            onChange={(e) => setDoc({ ...doc, show_download: e.target.checked })}
            data-testid="legal-pdf-show"
            className="h-4 w-4 accent-cmic-600 cursor-pointer"
          />
          Mostrar el botón de descarga en la página pública
        </label>

        <div className="flex items-center gap-3 flex-wrap">
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="legal-pdf-input"
            onChange={(e) => uploadPdf(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => pdfInputRef.current?.click()}
            disabled={uploadingPdf}
            data-testid="legal-pdf-upload-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-cmic-200 text-cmic-700 hover:bg-cmic-50 rounded-lg disabled:opacity-50"
          >
            {uploadingPdf ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
            {doc.pdf_url ? 'Reemplazar PDF' : 'Subir PDF'}
          </button>
          <button
            type="button"
            onClick={openPicker}
            data-testid="legal-pdf-pick-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-charcoal-700 hover:bg-gray-50 rounded-lg"
          >
            <FolderOpen size={14} /> Elegir de archivos
          </button>
          {doc.pdf_url && (
            <div className="inline-flex items-center gap-2 text-sm text-charcoal-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2" data-testid="legal-pdf-current">
              <FileText size={14} className="text-cmic-600" />
              <a href={doc.pdf_url} target="_blank" rel="noopener noreferrer" className="hover:underline max-w-[240px] truncate">
                {doc.pdf_filename || doc.pdf_url.split('/').pop()}
              </a>
              <button type="button" onClick={removePdf} title="Quitar PDF" data-testid="legal-pdf-remove"
                className="p-1 rounded hover:bg-red-50 text-red-600"><X size={13} /></button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Texto del botón (Español)">
            <input type="text" value={doc.download_label?.es || ''} onChange={(e) => updateDownloadLabel('es', e.target.value)}
              placeholder="Descargar PDF"
              data-testid="legal-pdf-label-es"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Button text (English)">
            <input type="text" value={doc.download_label?.en || ''} onChange={(e) => updateDownloadLabel('en', e.target.value)}
              placeholder="Download PDF"
              data-testid="legal-pdf-label-en"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </Field>
        </div>
      </div>

      {/* Chapters */}
      {doc.chapters.map((chapter, ci) => {
        const isOpen = openChapters.has(ci);
        return (
        <div key={ci} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`legal-chapter-${ci}`}>
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => toggleChapter(ci)}
              data-testid={`legal-chapter-${ci}-toggle`}
              className="flex items-center gap-2 text-sm font-semibold text-charcoal-700 flex-1 min-w-0 text-left hover:text-cmic-700"
            >
              <ChevronDown size={16} className={`flex-shrink-0 text-charcoal-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              <span className="bg-cmic-100 text-cmic-700 rounded px-2 py-0.5 text-xs flex-shrink-0">Cap. {ci + 1}</span>
              <span className="truncate">{chapter.title?.es || '(sin título)'}</span>
              <span className="text-xs font-normal text-charcoal-400 flex-shrink-0">· {(chapter.articles || []).length} artículos</span>
            </button>
            <button onClick={() => removeChapter(ci)} title="Eliminar capítulo"
              data-testid={`legal-chapter-${ci}-remove`}
              className="p-1.5 rounded hover:bg-red-50 text-red-600 flex-shrink-0"><Trash2 size={14} /></button>
          </div>
          {isOpen && (
          <div className="p-5 space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Título del capítulo (ES)">
                <input type="text" value={chapter.title?.es || ''} onChange={(e) => updateChapterTitle(ci, 'es', e.target.value)}
                  data-testid={`legal-chapter-${ci}-title-es`}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </Field>
              <Field label="Chapter title (EN)">
                <input type="text" value={chapter.title?.en || ''} onChange={(e) => updateChapterTitle(ci, 'en', e.target.value)}
                  data-testid={`legal-chapter-${ci}-title-en`}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </Field>
            </div>
            {(chapter.articles || []).map((article, ai) => (
              <div key={ai} className="border border-gray-100 rounded-lg p-3 space-y-2 bg-gray-50/30" data-testid={`legal-article-${ci}-${ai}`}>
                <div className="flex items-center gap-2 justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-charcoal-500">Artículo</span>
                    <input type="text" value={article.number || ''} onChange={(e) => updateArticle(ci, ai, 'number', e.target.value)}
                      placeholder="Primero / 1 / I"
                      data-testid={`legal-article-${ci}-${ai}-number`}
                      className="w-32 border border-gray-200 rounded px-2 py-1 text-xs font-semibold text-cmic-700" />
                  </div>
                  <button onClick={() => removeArticle(ci, ai)} title="Eliminar artículo"
                    data-testid={`legal-article-${ci}-${ai}-remove`}
                    className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <RichTextEditor
                    value={article.es || ''}
                    onChange={(html) => updateArticle(ci, ai, 'es', html)}
                    allowMedia={false}
                    minHeight={140}
                    testid={`legal-article-${ci}-${ai}-es`}
                    placeholder="Texto en español"
                  />
                  <RichTextEditor
                    value={article.en || ''}
                    onChange={(html) => updateArticle(ci, ai, 'en', html)}
                    allowMedia={false}
                    minHeight={140}
                    testid={`legal-article-${ci}-${ai}-en`}
                    placeholder="Text in English"
                  />
                </div>
              </div>
            ))}
            <button onClick={() => addArticle(ci)}
              data-testid={`legal-chapter-${ci}-add-article`}
              className="inline-flex items-center gap-1.5 text-cmic-600 hover:bg-cmic-50 rounded-lg px-3 py-2 text-sm font-medium">
              <Plus size={14} /> Agregar artículo
            </button>
          </div>
          )}
        </div>
        );
      })}
      <button onClick={addChapter}
        data-testid="legal-add-chapter"
        className="inline-flex items-center gap-1.5 px-4 py-2.5 border-2 border-dashed border-gray-300 hover:border-cmic-400 hover:text-cmic-700 text-charcoal-500 rounded-lg text-sm font-medium w-full justify-center">
        <Plus size={14} /> Agregar nuevo capítulo
      </button>

      {pickerOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" onClick={() => setPickerOpen(false)} data-testid="legal-pdf-picker">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-charcoal-800 flex items-center gap-2"><FolderOpen size={16} className="text-cmic-600" /> Elegir un PDF existente</h3>
              <button onClick={() => setPickerOpen(false)} className="p-1.5 rounded hover:bg-gray-100 text-charcoal-500" data-testid="legal-pdf-picker-close"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-2" style={{ overscrollBehavior: 'contain' }}>
              {mediaLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-cmic-500" size={24} /></div>
              ) : mediaFiles.length === 0 ? (
                <p className="text-sm text-charcoal-500 text-center py-12">No hay archivos PDF subidos todavía. Usa "Subir PDF" para agregar uno.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {mediaFiles.map((f) => (
                    <li key={f.filename}>
                      <button
                        type="button"
                        onClick={() => pickExisting(f)}
                        data-testid={`legal-pdf-pick-${f.filename.replace(/\W+/g, '-').toLowerCase()}`}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-cmic-50 rounded-lg ${doc.pdf_url === f.url ? 'bg-cmic-50' : ''}`}
                      >
                        <FileText size={16} className="text-cmic-600 flex-shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-sm text-charcoal-700">{f.filename}</span>
                        <span className="text-[11px] text-charcoal-400 flex-shrink-0">{(f.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-charcoal-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
